import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  type User,
  type UserCredential,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import { markIntentionalLogout } from "@labeling-tools/auth";
import { client as atlasClient } from "@labeling-tools/api/atlas/client.gen";
import { client as lebronClient } from "@labeling-tools/api/lebron/generated/client.gen";

let firebaseConfig;
try {
  firebaseConfig = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG);
} catch {
  throw new Error(
    "Invalid or missing VITE_FIREBASE_CONFIG environment variable. " +
      "Ensure .env.local exists with a valid JSON Firebase config.",
  );
}

initializeApp(firebaseConfig);
const auth = getAuth();
const provider = new GoogleAuthProvider();

function getCurrentUser(): Promise<User | null> {
  return new Promise((resolve) => {
    const remove = auth.onAuthStateChanged((user) => {
      remove();
      resolve(user);
    });
  });
}

lebronClient.setConfig({
  baseUrl:
    import.meta.env.VITE_LEBRON_BASE_URL || "https://mvp-api.augmoto.com",
});

atlasClient.setConfig({
  baseUrl:
    import.meta.env.VITE_ATLAS_BASE_URL || "https://atlas-api.augmoto.com",
});

let authHeadersSetup = false;
function setupAuthHeaders() {
  if (authHeadersSetup) return;
  authHeadersSetup = true;

  for (const apiClient of [lebronClient, atlasClient]) {
    apiClient.interceptors.request.use(async (config) => {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.set("Authorization", `Bearer ${token}`);
      }
      return config;
    });
  }
}

setupAuthHeaders();

async function signIn(): Promise<UserCredential> {
  return signInWithPopup(auth, provider);
}

async function signOut(): Promise<void> {
  markIntentionalLogout();
  return auth.signOut();
}

export { signIn, signOut, getCurrentUser, auth };
